import { type AccountKey, type MailboxInfo, getIndexerValueForMailboxInfo } from "owa-client-types";
import { Prefetchable, type PrefetchableNestedAppAuthExecutor, } from "./PrefetchableNestedAppAuthExecutor";
import type { NestedAppAuthExecutorConstructor } from "owa-nested-app-auth-types";
import { executeNaaRequest } from "./executeNaaRequest";

const executors = new Map<AccountKey, PrefetchableNestedAppAuthExecutor>();

export function getNestedAppAuthExecutorFromCache(Executor: NestedAppAuthExecutorConstructor, mailboxInfo: MailboxInfo): PrefetchableNestedAppAuthExecutor | undefined {
    const accountKey = getIndexerValueForMailboxInfo(mailboxInfo);
    
    // find existing executor for this account
    let executor = executors.get(accountKey);
    
    if (executor) {
        return executor;
    }
    
    // create a new executor using the provided factory if we haven't already
    const PrefetchableExecutor = Prefetchable(Executor);
    executor = new PrefetchableExecutor(mailboxInfo);
    
    if (executor) {
        executors.set(accountKey, executor);
    }
    
    return executor;
}

/**
 * Determines if request is a Nested App Auth request, and if it is it will process
 * the request using the specified executor
 * @param mailboxInfo The MailboxInfo for the account for which the request is to be executed
 * @param request NAA request payload; `BridgeRequestEnvelope` serialized as JSON
 * @param origin Origin URL of the app making the request
 * @returns Promise that resolves a string; `BridgeResponseEnvelope` serialized as JSON
 */
export function executeNaaRequestForMailboxInfo(Executor: NestedAppAuthExecutorConstructor, mailboxInfo: MailboxInfo, request: string, origin: string, isTrustedOrigin: boolean, targetWindow: Window, scenarioName: string, callerId: string): Promise<string> {
    const executor = getNestedAppAuthExecutorFromCache(Executor, mailboxInfo);
    
    if (executor) {
        return executeNaaRequest(request, executor, mailboxInfo, origin, isTrustedOrigin, targetWindow, scenarioName, callerId);
    }
    
    throw new Error("Unable to get NestedAppAuthExecutor");
}
